import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import {
  Palette, Plus, Trash2, Pencil, CheckCircle2, Power, CalendarClock,
  Eye, Image as ImageIcon, X, Sparkles, CalendarX, Upload, PanelLeft, ZoomIn, Crop,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { seasonalThemeAPI } from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import {
  SEASONAL_PRESETS, TEMPLATE_ORDER, EFFECT_OPTIONS,
  presetTokens, presetEffect, deriveCustomTokens, presetIntensity, presetHero,
  SIDEBAR_IMAGE_CHOICES, DEFAULT_SIDEBAR_INTENSITY,
} from '@/lib/seasonalThemes';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Recommended upload size for the sidebar artwork (tall/portrait, covers the
// full-height 256px-wide nav rail).
const SIDEBAR_IMG_SPEC = '1024 × 1536 px (portrait 2:3) · PNG, JPG or WebP · ≤ 50 MB';

// The sidebar rail is 256px wide × full height — a tall portrait slot. We crop
// uploads to this ratio so landscape photos frame correctly in the rail.
const SIDEBAR_CROP_ASPECT = 256 / 960;

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = url;
  });
}

async function getCroppedBlob(src, cropPx, maxW = 560) {
  const image = await createImage(src);
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, maxW / cropPx.width);
  canvas.width = Math.round(cropPx.width * scale);
  canvas.height = Math.round(cropPx.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, cropPx.x, cropPx.y, cropPx.width, cropPx.height, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9));
}

const STATUS_BADGE = {
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  scheduled: { label: 'Scheduled', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const templateLabel = (t) => SEASONAL_PRESETS[t]?.label || (t === 'custom' ? 'Custom' : t);

const toCssUrl = (v) => {
  if (!v || v === 'none') return 'none';
  const u = v.startsWith('/api') ? `${BACKEND}${v}` : v;
  return `url("${u}")`;
};

// Build the full token set from the current editor form, folding in the
// admin's chosen sidebar image + intensity.
function resolveTokens(form) {
  const base = form.template === 'custom'
    ? deriveCustomTokens(form.customColor || '#0ea5e9')
    : presetTokens(form.template);
  const tokens = { ...base };
  if (form.sidebarImage === 'none') {
    tokens['kr-sidebar-image'] = 'none';
  } else if (form.sidebarImage) {
    tokens['kr-sidebar-image'] = toCssUrl(form.sidebarImage);
  }
  tokens['kr-sidebar-image-opacity'] = String(
    form.sidebarIntensity ?? DEFAULT_SIDEBAR_INTENSITY);
  return tokens;
}

function emptyForm() {
  return {
    name: '',
    template: 'fall',
    customColor: '#0ea5e9',
    accent_effect: presetEffect('fall'),
    sidebarImage: '',        // '' = template default, 'none', gallery url, or /api url
    sidebarIntensity: DEFAULT_SIDEBAR_INTENSITY,
    banner_enabled: false,
    banner_label: '',
    button_enabled: false,
    button_label: '',
    button_link_type: 'internal',
    button_link_value: '',
  };
}

const PREVIEW_NAV = ['Dashboard', 'Clients', 'Pipeline', 'Reports', 'Settings'];

// Live, in-editor render of the themed sidebar rail (mirrors the real
// `.sidebar` + `::before` image + `::after` scrim treatment from App.css).
function SidebarPreview({ tokens }) {
  const hsl = (k, fb) => (tokens[k] ? (tokens[k].startsWith('hsl') || tokens[k].startsWith('#') ? tokens[k] : `hsl(${tokens[k]})`) : fb);
  const navy900 = hsl('kr-navy-900', '#0b1a33');
  const navy950 = hsl('kr-navy-950', '#081832');
  const gold = hsl('kr-gold-500', '#f5c542');
  const active = tokens['sidebar-active'] ? `hsl(${tokens['sidebar-active']})` : gold;
  const image = tokens['kr-sidebar-image'] || 'none';
  const opacity = parseFloat(tokens['kr-sidebar-image-opacity']) || DEFAULT_SIDEBAR_INTENSITY;
  return (
    <div
      data-testid="sidebar-live-preview"
      className="relative w-full h-64 rounded-xl overflow-hidden border border-slate-200 shadow-sm"
      style={{ background: `linear-gradient(180deg, ${navy900} 0%, ${navy950} 100%)` }}
    >
      <div className="absolute inset-0" aria-hidden
        style={{ backgroundImage: image, backgroundSize: 'cover', backgroundPosition: 'center', opacity }} />
      <div className="absolute inset-0" aria-hidden
        style={{ background: 'linear-gradient(180deg, rgba(5,8,20,0.35) 0%, rgba(5,8,20,0.62) 100%)' }} />
      <div className="relative z-10 p-3 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 pb-2 mb-1 border-b border-white/10">
          <span className="h-5 w-5 rounded" style={{ background: gold }} />
          <span className="text-white/90 text-xs font-semibold tracking-wide">KR AGENCY</span>
        </div>
        {PREVIEW_NAV.map((item, i) => (
          <div key={item}
            className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11px] font-medium"
            style={i === 0
              ? { background: 'rgba(255,255,255,0.10)', color: '#fff', boxShadow: `inset 3px 0 0 ${active}` }
              : { color: 'rgba(255,255,255,0.72)' }}>
            <span className="h-2.5 w-2.5 rounded-sm"
              style={{ background: i === 0 ? active : 'rgba(255,255,255,0.4)' }} />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

// Scaled-down mock of the WHOLE portal (sidebar + topbar + metric cards +
// pipeline) rendered with the resolved tokens, so admins can judge the full
// reskin — not just the rail — before activating.
function PortalPreview({ tokens }) {
  const hsl = (k, fb) => (tokens[k] ? (tokens[k].startsWith('hsl') || tokens[k].startsWith('#') ? tokens[k] : `hsl(${tokens[k]})`) : fb);
  const navy900 = hsl('kr-navy-900', '#0b1a33');
  const navy950 = hsl('kr-navy-950', '#081832');
  const gold = hsl('kr-gold-500', '#f5c542');
  const active = tokens['sidebar-active'] ? `hsl(${tokens['sidebar-active']})` : gold;
  const canvas = hsl('kr-canvas', '#f1f5f9');
  const surface = hsl('kr-surface', '#ffffff');
  const border = hsl('kr-border', '#e2e8f0');
  const text = hsl('kr-text', '#0f172a');
  const muted = hsl('kr-muted', '#64748b');
  const image = tokens['kr-sidebar-image'] || 'none';
  const opacity = parseFloat(tokens['kr-sidebar-image-opacity']) || DEFAULT_SIDEBAR_INTENSITY;
  const metrics = [['Revenue', '$84.2k'], ['Clients', '126'], ['Open', '18']];
  const cols = ['New', 'Active', 'Won'];
  return (
    <div
      data-testid="portal-live-preview"
      className="relative w-full h-64 rounded-xl overflow-hidden border border-slate-200 shadow-sm flex"
      style={{ background: canvas }}
    >
      {/* Sidebar rail */}
      <div className="relative w-16 shrink-0 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${navy900} 0%, ${navy950} 100%)` }}>
        <div className="absolute inset-0" aria-hidden
          style={{ backgroundImage: image, backgroundSize: 'cover', backgroundPosition: 'center', opacity }} />
        <div className="absolute inset-0" aria-hidden
          style={{ background: 'linear-gradient(180deg, rgba(5,8,20,0.35) 0%, rgba(5,8,20,0.62) 100%)' }} />
        <div className="relative z-10 p-1.5 flex flex-col gap-1">
          <span className="h-3 w-3 rounded mb-1" style={{ background: gold }} />
          {PREVIEW_NAV.map((item, i) => (
            <span key={item} className="h-1.5 rounded-sm"
              style={i === 0
                ? { background: active, width: '85%' }
                : { background: 'rgba(255,255,255,0.35)', width: '70%' }} />
          ))}
        </div>
      </div>
      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <div className="flex items-center justify-between px-3 h-9 shrink-0"
          style={{ background: surface, borderBottom: `1px solid ${border}` }}>
          <span className="h-2 w-20 rounded-full" style={{ background: border }} />
          <span className="h-5 w-14 rounded-md" style={{ background: gold }} />
        </div>
        {/* Body */}
        <div className="flex-1 p-2.5 space-y-2 overflow-hidden">
          <div className="grid grid-cols-3 gap-2">
            {metrics.map(([label, val], i) => (
              <div key={label} className="rounded-lg p-2"
                style={{ background: surface, border: `1px solid ${border}` }}>
                <span className="block h-4 w-4 rounded-md mb-1.5"
                  style={{ background: i === 1 ? gold : navy900 }} />
                <span className="block text-[11px] font-bold leading-none" style={{ color: text }}>{val}</span>
                <span className="block text-[8px] mt-0.5" style={{ color: muted }}>{label}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {cols.map((c) => (
              <div key={c} className="rounded-lg overflow-hidden"
                style={{ background: surface, border: `1px solid ${border}` }}>
                <div className="px-2 py-1 text-[8px] font-semibold text-white"
                  style={{ background: navy900 }}>{c}</div>
                <div className="p-1.5 space-y-1">
                  <span className="block h-3 rounded" style={{ background: canvas }} />
                  <span className="block h-3 rounded w-3/4" style={{ background: canvas }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SeasonalThemesPage() {
  const { refreshSeason, previewSeason, clearSeasonPreview } = useTheme();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // theme_id or null (new)
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewMode, setPreviewMode] = useState('sidebar'); // 'sidebar' | 'portal'

  const [scheduleFor, setScheduleFor] = useState(null); // theme object
  const [sched, setSched] = useState({ start_local: '', end_local: '', tz: 'America/New_York' });

  // Sidebar-image crop flow.
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropObjectUrl, setCropObjectUrl] = useState(false); // cropSrc is a blob: URL we own
  const [cropOriginalFile, setCropOriginalFile] = useState(null); // raw file to persist for re-crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropUploading, setCropUploading] = useState(false);

  const closeCrop = () => {
    if (cropObjectUrl && cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropObjectUrl(false);
    setCropOriginalFile(null);
    setCropOpen(false);
  };

  const resetCropFraming = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const onPickSidebarFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropSrc(URL.createObjectURL(file));
    setCropObjectUrl(true);
    setCropOriginalFile(file);
    resetCropFraming();
    setCropOpen(true);
    e.target.value = ''; // allow re-selecting the same file
  };

  // Re-open the cropper on the already-uploaded image (uses the untouched
  // original when available so repeated crops never degrade quality).
  const handleRecrop = () => {
    const ex = form._existing;
    const path = ex?.sidebar_original_url || ex?.sidebar_image_url;
    if (!editing || !path) return;
    // Use the relative /api path so the image is always same-origin as the
    // page (ingress routes /api -> backend). This avoids canvas taint when the
    // app runs on a domain different from REACT_APP_BACKEND_URL.
    setCropSrc(path.startsWith('/api') ? path : `${BACKEND}${path}`);
    setCropObjectUrl(false);
    setCropOriginalFile(null); // keep the stored original on the server
    resetCropFraming();
    setCropOpen(true);
  };

  const applyCrop = async () => {
    if (!croppedAreaPixels || !editing) return;
    setCropUploading(true);
    try {
      const blob = await getCroppedBlob(cropSrc, croppedAreaPixels);
      const file = new File([blob], `sidebar-${Date.now()}.jpg`, { type: 'image/jpeg' });
      await handleSidebarImage(editing, file, cropOriginalFile);
      closeCrop();
    } catch {
      toast.error('Could not crop image');
    } finally {
      setCropUploading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await seasonalThemeAPI.list();
      setThemes(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load themes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Always revert any live preview when LEAVING the page — only on true
  // unmount. We keep the latest clearer in a ref so this effect's cleanup
  // never fires on every ThemeProvider re-render (which would otherwise
  // re-apply the previously-active season after a deactivate).
  const clearPreviewRef = useRef(clearSeasonPreview);
  clearPreviewRef.current = clearSeasonPreview;
  useEffect(() => () => clearPreviewRef.current(), []);

  const activeTheme = useMemo(() => themes.find((t) => t.status === 'active'), [themes]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  };

  const openEdit = (t) => {
    const tokenImg = t.tokens?.['kr-sidebar-image'];
    const uploadedUrl = t.sidebar_image_url || '';
    let sidebarImage = '';
    if (tokenImg === 'none') {
      sidebarImage = 'none';
    } else if (tokenImg) {
      const m = tokenImg.match(/url\(["']?(.*?)["']?\)/);
      const raw = m && m[1] ? m[1] : '';
      const idx = raw.indexOf('/api/seasonal-themes/');
      const normalized = idx >= 0 ? raw.slice(idx) : raw;
      if (uploadedUrl && normalized.split('?')[0] === uploadedUrl.split('?')[0]) {
        sidebarImage = uploadedUrl;                  // the uploaded custom image
      } else if (SIDEBAR_IMAGE_CHOICES.some((c) => c.url === normalized)) {
        sidebarImage = normalized;                   // an explicit gallery pick
      } else {
        sidebarImage = '';                           // preset / theme default
      }
    }
    setEditing(t.theme_id);
    setForm({
      name: t.name || '',
      template: t.template || 'custom',
      customColor: '#0ea5e9',
      accent_effect: t.accent_effect || 'none',
      sidebarImage,
      sidebarIntensity: parseFloat(t.tokens?.['kr-sidebar-image-opacity']) || DEFAULT_SIDEBAR_INTENSITY,
      banner_enabled: !!t.banner_enabled,
      banner_label: t.banner_label || '',
      button_enabled: !!t.button_enabled,
      button_label: t.button_label || '',
      button_link_type: t.button_link_type || 'internal',
      button_link_value: t.button_link_value || '',
      _existing: t,
    });
    setEditorOpen(true);
  };

  const onTemplateChange = (template) => {
    setForm((f) => ({
      ...f,
      template,
      accent_effect: template === 'custom' ? f.accent_effect : presetEffect(template),
    }));
  };

  const doPreview = () => {
    previewSeason({
      tokens: resolveTokens(form),
      template: form.template,
      accent_effect: form.accent_effect,
    });
    setPreviewing(true);
    toast.message('Preview applied', { description: 'It will revert when you close or save.' });
  };

  const stopPreview = () => { clearSeasonPreview(); setPreviewing(false); };

  // Keep the live portal preview in sync while the admin tweaks the theme.
  useEffect(() => {
    if (!previewing) return;
    previewSeason({
      tokens: resolveTokens(form),
      template: form.template,
      accent_effect: form.accent_effect,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.template, form.customColor, form.accent_effect, form.sidebarImage, form.sidebarIntensity, previewing]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Give the theme a name'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      template: form.template,
      tokens: resolveTokens(form),
      accent_effect: form.accent_effect,
      banner_enabled: form.banner_enabled,
      banner_label: form.banner_label || null,
      button_enabled: form.button_enabled,
      button_label: form.button_label || null,
      button_link_type: form.button_link_type,
      button_link_value: form.button_link_value || null,
    };
    try {
      if (editing) {
        await seasonalThemeAPI.update(editing, payload);
        toast.success('Theme updated');
      } else {
        await seasonalThemeAPI.create(payload);
        toast.success('Theme created');
      }
      stopPreview();
      setEditorOpen(false);
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleSidebarImage = async (themeId, file, original) => {
    if (!file) return;
    try {
      const { data } = await seasonalThemeAPI.uploadSidebarImage(themeId, file, original);
      toast.success('Sidebar image uploaded');
      setForm((f) => ({ ...f, sidebarImage: data?.sidebar_image_url || f.sidebarImage, _existing: data || f._existing }));
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not upload image');
    }
  };

  const handleImage = async (themeId, file) => {
    if (!file) return;
    try {
      await seasonalThemeAPI.uploadImage(themeId, file);
      toast.success('Banner image uploaded');
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not upload image');
    }
  };

  const handleActivate = async (t) => {
    try {
      await seasonalThemeAPI.activate(t.theme_id);
      toast.success(`"${t.name}" activated`);
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate');
    }
  };

  const handleDeactivate = async (t) => {
    try {
      await seasonalThemeAPI.deactivate(t.theme_id);
      toast.success('Theme deactivated');
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not deactivate');
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete the theme "${t.name}"?`)) return;
    try {
      await seasonalThemeAPI.delete(t.theme_id);
      toast.success('Theme deleted');
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const openSchedule = (t) => {
    setScheduleFor(t);
    setSched({
      start_local: t.schedule_start_local || '',
      end_local: t.schedule_end_local || '',
      tz: t.schedule_tz || 'America/New_York',
    });
  };

  const handleSchedule = async () => {
    if (!sched.start_local) { toast.error('Pick a start date/time'); return; }
    try {
      await seasonalThemeAPI.schedule(scheduleFor.theme_id, sched);
      toast.success('Theme scheduled and approved');
      setScheduleFor(null);
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not schedule');
    }
  };

  const handleUnschedule = async (t) => {
    try {
      await seasonalThemeAPI.unschedule(t.theme_id);
      toast.success('Schedule cancelled');
      await load();
      await refreshSeason();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="seasonal-themes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <Palette className="h-6 w-6 text-brand" /> Seasonal Themes
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Restyle the entire portal with a seasonal theme. Nothing turns on by
            itself: a theme only goes live when you approve it manually, or when
            you schedule it with a date range (which also counts as your approval).
          </p>
        </div>
        <Button onClick={openNew} className="gap-1.5" data-testid="new-theme-btn">
          <Plus className="h-4 w-4" /> New theme
        </Button>
      </div>

      {activeTheme && (
        <Card className="border-emerald-200 bg-emerald-50/50" data-testid="active-theme-banner">
          <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Active theme: {activeTheme.name}
                </div>
                <div className="text-xs text-slate-500">
                  {templateLabel(activeTheme.template)}
                  {activeTheme.activated_by ? ` · activated by ${activeTheme.activated_by}` : ''}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5"
              onClick={() => handleDeactivate(activeTheme)} data-testid="deactivate-active-btn">
              <Power className="h-4 w-4" /> Deactivate
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-sm text-slate-400 py-12 text-center">Loading themes…</div>
      ) : themes.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl" data-testid="themes-empty">
          <Sparkles className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No themes yet. Create your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="themes-grid">
          {themes.map((t) => {
            const badge = STATUS_BADGE[t.status] || STATUS_BADGE.draft;
            const sw = SEASONAL_PRESETS[t.template]?.swatch;
            const brand = t.tokens?.brand;
            return (
              <Card key={t.theme_id} data-testid={`theme-card-${t.theme_id}`} className="overflow-hidden">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-9 w-9 rounded-lg shrink-0 border border-black/10"
                        style={{ background: brand ? `hsl(${brand})` : (sw || '#64748b') }} />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{t.name}</div>
                        <div className="text-xs text-slate-500">{templateLabel(t.template)}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className={badge.cls} data-testid={`theme-status-${t.theme_id}`}>
                      {badge.label}
                    </Badge>
                  </div>

                  {t.status === 'scheduled' && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5 flex items-center gap-1.5">
                      <CalendarClock className="h-3.5 w-3.5" />
                      {t.schedule_start_local || '—'}{t.schedule_end_local ? ` → ${t.schedule_end_local}` : ''} ({t.schedule_tz})
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 flex-wrap pt-1">
                    {t.status === 'active' ? (
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => handleDeactivate(t)}
                        data-testid={`deactivate-btn-${t.theme_id}`}>
                        <Power className="h-3.5 w-3.5" /> Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" className="gap-1" onClick={() => handleActivate(t)}
                        data-testid={`activate-btn-${t.theme_id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve &amp; activate
                      </Button>
                    )}
                    {t.status === 'scheduled' ? (
                      <Button size="sm" variant="ghost" className="gap-1" onClick={() => handleUnschedule(t)}
                        data-testid={`unschedule-btn-${t.theme_id}`}>
                        <CalendarX className="h-3.5 w-3.5" /> Cancel schedule
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="gap-1" onClick={() => openSchedule(t)}
                        data-testid={`schedule-btn-${t.theme_id}`}>
                        <CalendarClock className="h-3.5 w-3.5" /> Schedule
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="gap-1" onClick={() => openEdit(t)}
                      data-testid={`edit-btn-${t.theme_id}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1 text-rose-600 hover:text-rose-700"
                      onClick={() => handleDelete(t)} data-testid={`delete-btn-${t.theme_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── Editor dialog ─────────────────────────────────────────── */}
      <Dialog open={editorOpen} onOpenChange={(o) => { if (!o) { stopPreview(); } setEditorOpen(o); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="theme-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit theme' : 'New seasonal theme'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Fall 2026" data-testid="theme-name-input" />
            </div>

            <div>
              <Label>Template</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {TEMPLATE_ORDER.map((tpl) => {
                  const active = form.template === tpl;
                  const sw = SEASONAL_PRESETS[tpl]?.swatch || '#64748b';
                  return (
                    <button key={tpl} type="button" onClick={() => onTemplateChange(tpl)}
                      data-testid={`template-opt-${tpl}`}
                      className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition-colors ${active ? 'border-brand ring-2 ring-brand/30 bg-brand/5' : 'border-slate-200 hover:border-slate-300'}`}>
                      <span className="h-4 w-4 rounded-full border border-black/10 shrink-0" style={{ background: sw }} />
                      <span className="truncate">{templateLabel(tpl)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {form.template !== 'custom' && (
              <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="template-preview">
                {presetHero(form.template) && (
                  <div className="relative h-28 w-full">
                    <img src={presetHero(form.template)} alt="theme hero"
                      className="h-full w-full object-cover" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.35), transparent)' }} />
                  </div>
                )}
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                  <span className="text-xs text-slate-500">{templateLabel(form.template)}</span>
                  <Badge variant="outline" className={presetIntensity(form.template) === 'strong'
                    ? 'bg-brand/10 text-brand border-brand/30' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                    {presetIntensity(form.template) === 'strong' ? 'Strong theme' : 'Subtle detail'}
                  </Badge>
                </div>
              </div>
            )}

            {form.template === 'custom' && (
              <div className="flex items-center gap-3">
                <Label className="shrink-0">Brand color</Label>
                <input type="color" value={form.customColor}
                  onChange={(e) => setForm((f) => ({ ...f, customColor: e.target.value }))}
                  className="h-9 w-16 rounded cursor-pointer border border-slate-200"
                  data-testid="custom-color-input" />
                <span className="text-xs text-slate-400">{form.customColor}</span>
              </div>
            )}

            <div>
              <Label>Decorative effect</Label>
              <Select value={form.accent_effect} onValueChange={(v) => setForm((f) => ({ ...f, accent_effect: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="accent-effect-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EFFECT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ─── Sidebar background image ─────────────────────────── */}
            <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="sidebar-image-section">
              <Label className="flex items-center gap-1.5"><PanelLeft className="h-4 w-4" /> Sidebar background image</Label>

              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid="preview-mode-toggle">
                <button type="button" onClick={() => setPreviewMode('sidebar')}
                  data-testid="preview-mode-sidebar"
                  className={`px-3 py-1 text-[11px] font-medium rounded-md transition-colors ${previewMode === 'sidebar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                  Sidebar
                </button>
                <button type="button" onClick={() => setPreviewMode('portal')}
                  data-testid="preview-mode-portal"
                  className={`px-3 py-1 text-[11px] font-medium rounded-md transition-colors ${previewMode === 'portal' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                  Full portal
                </button>
              </div>

              {previewMode === 'portal'
                ? <PortalPreview tokens={resolveTokens(form)} />
                : <SidebarPreview tokens={resolveTokens(form)} />}

              <div>
                <p className="text-xs font-medium text-slate-500 mb-1.5">Choose from the gallery</p>
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => setForm((f) => ({ ...f, sidebarImage: '' }))}
                    data-testid="sidebar-img-default"
                    className={`relative h-16 rounded-lg border-2 text-[11px] font-medium flex items-center justify-center bg-slate-50 ${form.sidebarImage === '' ? 'border-brand ring-2 ring-brand/30' : 'border-slate-200'}`}>
                    Theme default
                  </button>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, sidebarImage: 'none' }))}
                    data-testid="sidebar-img-none"
                    className={`relative h-16 rounded-lg border-2 text-[11px] font-medium flex items-center justify-center bg-slate-50 ${form.sidebarImage === 'none' ? 'border-brand ring-2 ring-brand/30' : 'border-slate-200'}`}>
                    No image
                  </button>
                  {SIDEBAR_IMAGE_CHOICES.map((c) => (
                    <button key={c.key} type="button" onClick={() => setForm((f) => ({ ...f, sidebarImage: c.url }))}
                      data-testid={`sidebar-img-${c.key}`}
                      title={c.label}
                      className={`relative h-16 rounded-lg border-2 overflow-hidden ${form.sidebarImage === c.url ? 'border-brand ring-2 ring-brand/30' : 'border-slate-200'}`}>
                      <img src={c.url} alt={c.label} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-slate-500 mb-1.5">Or upload your own</p>
                {editing ? (
                  <label htmlFor="sidebar-upload-input"
                    className="flex flex-col items-center justify-center gap-1 w-full rounded-lg border-2 border-dashed border-slate-300 hover:border-brand hover:bg-brand/5 cursor-pointer py-5 px-3 text-center transition-colors"
                    data-testid="sidebar-image-upload-label">
                    <Upload className="h-5 w-5 text-brand" />
                    <span className="text-xs font-semibold text-slate-700">Click to upload an image</span>
                    <span className="text-[11px] text-slate-400" data-testid="sidebar-image-dimensions">
                      Recommended: {SIDEBAR_IMG_SPEC}
                    </span>
                    <input id="sidebar-upload-input" type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                      onChange={onPickSidebarFile}
                      data-testid="sidebar-image-input" />
                  </label>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-1 w-full rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 py-5 px-3 text-center"
                    data-testid="sidebar-image-upload-disabled">
                    <Upload className="h-5 w-5 text-slate-300" />
                    <span className="text-xs font-medium text-slate-500">Save the theme first, then upload a custom image</span>
                    <span className="text-[11px] text-slate-400" data-testid="sidebar-image-dimensions">
                      Recommended: {SIDEBAR_IMG_SPEC}
                    </span>
                  </div>
                )}
                {editing && form._existing?.sidebar_image_url
                  && form.sidebarImage === form._existing.sidebar_image_url && (
                  <button type="button" onClick={handleRecrop}
                    data-testid="sidebar-recrop-btn"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-brand hover:underline">
                    <Crop className="h-3.5 w-3.5" /> Re-crop current image
                  </button>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-sm">Image intensity</Label>
                  <span className="text-xs tabular-nums text-slate-500" data-testid="sidebar-intensity-value">
                    {Math.round((form.sidebarIntensity ?? DEFAULT_SIDEBAR_INTENSITY) * 100)}%
                  </span>
                </div>
                <Slider min={0} max={1} step={0.02}
                  value={[form.sidebarIntensity ?? DEFAULT_SIDEBAR_INTENSITY]}
                  onValueChange={(v) => setForm((f) => ({ ...f, sidebarIntensity: v[0] }))}
                  data-testid="sidebar-intensity-slider" />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>Subtle</span><span>Immersive</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><ImageIcon className="h-4 w-4" /> Dashboard banner</Label>
                <Switch checked={form.banner_enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, banner_enabled: v }))}
                  data-testid="banner-enabled-switch" />
              </div>
              {form.banner_enabled && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-400">Added to the Dashboard carousel (does not replace existing banners).</p>
                  <Input value={form.banner_label}
                    onChange={(e) => setForm((f) => ({ ...f, banner_label: e.target.value }))}
                    placeholder="Banner text (if you don't upload an image)" data-testid="banner-label-input" />
                  {editing && form._existing?.banner_image_url && (
                    <img src={`${BACKEND}${form._existing.banner_image_url}`} alt="banner"
                      className="w-full rounded-md border border-slate-200" />
                  )}
                  {editing ? (
                    <div>
                      <Label className="text-xs text-slate-500">Banner image (optional)</Label>
                      <Input type="file" accept="image/*" className="mt-1"
                        onChange={(e) => handleImage(editing, e.target.files?.[0])}
                        data-testid="banner-image-input" />
                      <p className="text-[11px] text-slate-400 mt-1" data-testid="banner-image-dimensions">
                        Same as dashboard banners — very wide format (e.g. <b>7968×200 px</b>), shown edge-to-edge keeping its aspect ratio. PNG/JPEG/WebP/GIF.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Save the theme first to upload an image.</p>
                  )}
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Action button</Label>
                    <Switch checked={form.button_enabled}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, button_enabled: v }))}
                      data-testid="banner-button-switch" />
                  </div>
                  {form.button_enabled && (
                    <div className="grid grid-cols-1 gap-2">
                      <Input value={form.button_label}
                        onChange={(e) => setForm((f) => ({ ...f, button_label: e.target.value }))}
                        placeholder="Button text" data-testid="button-label-input" />
                      <div className="flex gap-2">
                        <Select value={form.button_link_type}
                          onValueChange={(v) => setForm((f) => ({ ...f, button_link_type: v }))}>
                          <SelectTrigger className="w-32" data-testid="button-link-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="internal">Internal</SelectItem>
                            <SelectItem value="external">External</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input value={form.button_link_value}
                          onChange={(e) => setForm((f) => ({ ...f, button_link_value: e.target.value }))}
                          placeholder={form.button_link_type === 'external' ? 'https://…' : '/path'}
                          data-testid="button-link-value-input" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {previewing ? (
              <Button variant="outline" className="gap-1.5" onClick={stopPreview} data-testid="stop-preview-btn">
                <X className="h-4 w-4" /> Remove preview
              </Button>
            ) : (
              <Button variant="outline" className="gap-1.5" onClick={doPreview} data-testid="preview-theme-btn">
                <Eye className="h-4 w-4" /> Preview
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} data-testid="save-theme-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Schedule dialog ───────────────────────────────────────── */}
      <Dialog open={!!scheduleFor} onOpenChange={(o) => { if (!o) setScheduleFor(null); }}>
        <DialogContent className="max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule &amp; approve</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-xs text-slate-500">
              Scheduling approves the theme: it will turn on automatically when the
              start date arrives and turn off at the end date. You can force it
              manually at any time with "Approve &amp; activate".
            </p>
            <div>
              <Label>Start</Label>
              <Input type="datetime-local" value={sched.start_local}
                onChange={(e) => setSched((s) => ({ ...s, start_local: e.target.value }))}
                data-testid="schedule-start-input" />
            </div>
            <div>
              <Label>End (optional)</Label>
              <Input type="datetime-local" value={sched.end_local}
                onChange={(e) => setSched((s) => ({ ...s, end_local: e.target.value }))}
                data-testid="schedule-end-input" />
            </div>
            <div>
              <Label>Time zone</Label>
              <Select value={sched.tz} onValueChange={(v) => setSched((s) => ({ ...s, tz: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="schedule-tz-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">America/New_York (ET)</SelectItem>
                  <SelectItem value="America/Chicago">America/Chicago (CT)</SelectItem>
                  <SelectItem value="America/Denver">America/Denver (MT)</SelectItem>
                  <SelectItem value="America/Los_Angeles">America/Los_Angeles (PT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleFor(null)} data-testid="schedule-cancel-btn">Cancel</Button>
            <Button onClick={handleSchedule} data-testid="schedule-confirm-btn">Schedule &amp; approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Sidebar image crop dialog ─────────────────────────────── */}
      <Dialog open={cropOpen} onOpenChange={(o) => { if (!o) closeCrop(); else setCropOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="sidebar-crop-dialog">
          <DialogHeader>
            <DialogTitle>Crop for the sidebar</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            The sidebar is a tall vertical rail, so most photos need framing. Drag
            to reposition and zoom to choose exactly what shows.
          </p>
          <div className="relative w-full h-80 bg-slate-900 rounded-lg overflow-hidden" data-testid="sidebar-crop-area">
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={SIDEBAR_CROP_ASPECT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, px) => setCroppedAreaPixels(px)}
              />
            )}
          </div>
          <div>
            <Label className="text-sm flex items-center gap-1.5 mb-1.5"><ZoomIn className="h-4 w-4" /> Zoom</Label>
            <Slider min={1} max={3} step={0.01} value={[zoom]}
              onValueChange={(v) => setZoom(v[0])} data-testid="sidebar-crop-zoom" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCrop} data-testid="sidebar-crop-cancel">Cancel</Button>
            <Button onClick={applyCrop} disabled={cropUploading || !croppedAreaPixels} data-testid="sidebar-crop-apply">
              {cropUploading ? 'Uploading…' : 'Crop & upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
